import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI, s_PL } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { syncExerciseEntry } from '../../shared/dbService';
import { CLAUDE_HEADERS } from '../../shared/api';
import { AI_PARTNER, SEED_USERS } from '../../shared/constants';
import Avatar from '../../components/ui/Avatar';
import PreSessionReflection from './components/PreSessionReflection';
import ExercisePrep from './components/ExercisePrep';
import ExerciseSession from './components/ExerciseSession';
import SnelleResetSession from './components/SnelleResetSession';
import TerugNaarJouwKrachtSession from './components/TerugNaarJouwKrachtSession';
import ManipulationProtectionSession from './components/ManipulationProtectionSession';
import GrowthEffects from './components/GrowthEffects';
import MultiplayerInviteModal from '../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../modals/MultiplayerResults';
import { EXERCISES, EXERCISE_CATS, EXERCISE_GROWTH_MAP } from './constants';
import { GROWTH_CATEGORIES } from '../goals/constants';
import { playSound } from '../../shared/soundEngine';
import { GlowCard, TapButton } from '../../shared/interactive';
import LevelUpModal from '../../modals/LevelUpModal';
import ReadTab from './ReadTab';
import ProgramsTab from './ProgramsTab';

const SESSION_KEY = 'sl_active_session';

/** Mirrors the GoalsScreen logic — checks if catId is ready to level up. */
function checkLevelUp(catId, currentLevel, exHistory) {
  if (currentLevel >= 5) return false;
  const cat = GROWTH_CATEGORIES.find(c => c.id === catId);
  if (!cat) return false;
  const levelDef = cat.levels[currentLevel - 1];
  if (!levelDef) return false;
  const windowMs = 30 * 24 * 3600 * 1000;
  const recent = exHistory.filter(h => h.catId === catId && h.level === currentLevel && h.date > Date.now() - windowMs);
  const weightedSessions = recent.reduce((a, h) => a + (h.xp === 3 ? 1.5 : h.xp === 2 ? 1 : 0.5), 0);
  if (weightedSessions < levelDef.sessions) return false;
  const days = new Set(recent.map(h => new Date(h.date).toDateString())).size;
  if (days < levelDef.days) return false;
  const withFeelings = recent.filter(h => h.feelings && h.feelings.length > 0);
  if (withFeelings.length < Math.ceil(recent.length * 0.4)) return false;
  return true;
}

/** Persist / clear the active session so it survives tab switches. */
function saveSession(sel, preData) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ selId: sel.id, preData: preData || null }));
  } catch {}
}
function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}
function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const { selId, preData } = JSON.parse(raw);
    const sel = EXERCISES.find(e => e.id === selId);
    return sel ? { sel, preData } : null;
  } catch { return null; }
}

function ExerciseScreen({lang, allUsers, friends, license, walletBalance, onSpendCredit}) {
  const nl = lang==="nl";
  const effectiveLicense = license || localStorage.getItem('sl_license') || 'freemium';
  const [screenTab, setScreenTab] = useState('exercise'); // 'exercise' | 'read' | 'programs'
  const [view, setView] = useState("list"); // list | detail | prereflect | trackpick | prep | session
  const [sel, setSel] = useState(null);
  const [catFilt, setCatFilt] = useState("all");
  const [exHistory, setExHistory] = useStorage("sl_ex_history", []);
  const [levelUpQueue, setLevelUpQueue] = useState([]);
  const [preData, setPreData] = useState(null);
  const [partner, setPartner] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [savedSession, setSavedSession] = useState(() => loadSession()); // check once on mount
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [labTracks, setLabTracks] = useState([]);
  const [creditError, setCreditError] = useState(false);

  // Load lab tracks for paid users
  useEffect(() => {
    if (effectiveLicense === 'freemium') return;
    fetch(`${process.env.REACT_APP_API_URL || '/api'}/lab/tracks`)
      .then(r => r.json()).then(d => setLabTracks(d.tracks || [])).catch(() => {});
  }, [effectiveLicense]); // eslint-disable-line react-hooks/exhaustive-deps


  // Persist session whenever we enter/stay in session view
  useEffect(() => {
    if (view === 'session' && sel) saveSession(sel, preData);
  }, [view, sel, preData]);

  const handleFinishSession = ({feelings, aiComment, partnerScore, myScore}) => {
    playSound('exercise_done');
    clearSession(); setSavedSession(null);
    const growthContribs = EXERCISE_GROWTH_MAP[sel.id] || {};
    let catLevels_ = {};
    try { catLevels_ = JSON.parse(localStorage.getItem("sl_cat_levels")||"{}"); } catch {}

    // Save one history entry per contributing category, weighted by xp
    const newEntries = Object.entries(growthContribs).map(([catId, xp]) => ({
      exId: sel.id, catId, level: catLevels_[catId] || 1,
      xp, date: Date.now(), feelings, preData,
    }));

    // Build the updated history array to check level-ups immediately (before React re-render)
    let prevHistory = [];
    try { prevHistory = JSON.parse(localStorage.getItem("sl_ex_history") || "[]"); } catch {}
    const updatedHistory = [...newEntries, ...prevHistory];
    setExHistory(updatedHistory);
    newEntries.forEach(e => syncExerciseEntry(e));

    // Check each contributing category for a growth level-up
    const ups = [];
    Object.keys(growthContribs).forEach(catId => {
      const cur = catLevels_[catId] || 1;
      if (cur >= 5) return;
      if (checkLevelUp(catId, cur, updatedHistory)) {
        const newLv = cur + 1;
        catLevels_[catId] = newLv;
        const cat = GROWTH_CATEGORIES.find(c => c.id === catId);
        if (cat) ups.push({ category: cat, newLevel: newLv });
      }
    });
    if (ups.length > 0) {
      try { localStorage.setItem("sl_cat_levels", JSON.stringify(catLevels_)); } catch {}
      playSound('gift');
      setLevelUpQueue(ups);
    }

    if (partner) {
      setShowResults({myScore: myScore||85, partnerScore: partnerScore||Math.floor(Math.random()*30)+55, aiComment});
    } else {
      setView("list"); setSel(null); setPreData(null); setPartner(null);
    }
  };

  if (showResults && sel) {
    return (
      <>
        <div style={{minHeight:"60vh",padding:18,opacity:0.3,pointerEvents:"none",background:_T.bg}}>
          <div style={{fontFamily:"Georgia,serif",fontSize:20,color:C.white}}>{nl?sel.label:sel.labelEN}</div>
        </div>
        <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel={nl?"voltooiing%":"completion%"} partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel={nl?"voltooiing%":"completion%"} aiComment={showResults.aiComment} onClose={()=>{setShowResults(null);setView("list");setSel(null);setPreData(null);setPartner(null);}} onRematch={()=>{setShowResults(null);setView("prereflect");}}/>
      </>
    );
  }

  if (view==="session" && sel) {
    return (
      <div style={{minHeight:"100vh",background:_T.bg}}>
        <style>{CSS}</style>
        <div style={{position:"relative",height:120,overflow:"hidden"}}>
          <div style={{position:"absolute",inset:0,background:`linear-gradient(160deg,${sel.bgColor},#020d0b)`}}/>
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:80,opacity:0.08}}>{sel.bgEmoji}</div>
          <div style={{position:"absolute",bottom:14,left:20}}>
            <div style={{color:C.white,fontFamily:"Georgia,serif",fontSize:18}}>{nl?sel.label:sel.labelEN}</div>
          </div>
        </div>
        <div style={{padding:18}}>
          {sel.id === 'snelle_reset'
            ? <SnelleResetSession lang={lang} onFinish={handleFinishSession}/>
            : sel.id === 'terug_naar_kracht'
            ? <TerugNaarJouwKrachtSession lang={lang} onFinish={handleFinishSession}/>
            : sel.id === 'manipulation_protection'
            ? <ManipulationProtectionSession lang={lang} onFinish={handleFinishSession}/>
            : <ExerciseSession exercise={sel} lang={lang} preData={preData} partner={partner} license={effectiveLicense} selectedTrack={selectedTrack} onFinish={handleFinishSession}/>
          }
        </div>
      </div>
    );
  }

  if (view==="trackpick" && sel) {
    const TRACK_CATS = [{id:'focus',icon:'🎯'},{id:'energy',icon:'⚡'},{id:'calm',icon:'🌊'},{id:'motivation',icon:'🔥'},{id:'ambient',icon:'🌙'}];
    return (
      <div style={{minHeight:"100vh",background:_T.bg,padding:18}}>
        <style>{CSS}</style>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
          <button onClick={()=>setView("prereflect")} style={{background:"none",border:"none",color:C.silver,cursor:"pointer",fontSize:20}}>←</button>
          <div>
            <div style={{fontFamily:'Georgia,serif',fontSize:17,color:C.white}}>🎵 {nl?"Kies een track":"Pick a track"}</div>
            <div style={{color:C.silver,fontSize:11}}>{nl?"Optioneel — sla over om direct te starten":"Optional — skip to start directly"}</div>
          </div>
        </div>
        <div style={{...s_GC({padding:14,marginBottom:10}),border:`1px solid ${_T.cardBorderBot}`,display:'flex',alignItems:'center',gap:10,cursor:'pointer',opacity:selectedTrack?0.6:1}} onClick={()=>{setSelectedTrack(null);setView("prep");}}>
          <span style={{fontSize:20}}>🚫</span>
          <span style={{color:C.silver,fontSize:13}}>{nl?"Geen track":"No track"}</span>
          {!selectedTrack && <span style={{marginLeft:'auto',color:_T.accent}}>✓</span>}
        </div>
        {labTracks.map(t => {
          const cat = TRACK_CATS.find(c=>c.id===t.category)||TRACK_CATS[0];
          return (
            <div key={t.id} style={{...s_GC({padding:14,marginBottom:8}),border:`1px solid ${selectedTrack?.id===t.id?_T.accent:_T.cardBorderBot}`,cursor:'pointer'}} onClick={()=>setSelectedTrack(t)}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:20}}>{cat.icon}</span>
                <div style={{flex:1}}>
                  <div style={{color:C.white,fontSize:13,fontWeight:600}}>{t.title}</div>
                  {t.artist&&<div style={{color:C.silver,fontSize:11}}>{t.artist}</div>}
                </div>
                {selectedTrack?.id===t.id&&<span style={{color:_T.accent}}>✓</span>}
              </div>
            </div>
          );
        })}
        <button style={s_PB({width:'100%',marginTop:10,fontSize:15})} onClick={()=>setView("prep")}>
          {selectedTrack ? (nl?`▶ Start met "${selectedTrack.title}"`:`▶ Start with "${selectedTrack.title}"`) : (nl?'▶ Start zonder track':'▶ Start without track')}
        </button>
      </div>
    );
  }

  if (view==="prep" && sel) {
    return <ExercisePrep exercise={sel} lang={lang} onReady={()=>setView("session")}/>;
  }

  if (view==="prereflect" && sel) {
    return (
      <div style={{minHeight:"100vh",background:_T.bg,padding:18}}>
        <style>{CSS}</style>
        {showInvite && (
          <MultiplayerInviteModal lang={lang} title={nl?sel.label:sel.labelEN} icon={sel.icon} allUsers={allUsers} friends={friends}
            onInvite={(pid,mode)=>{const p=[...SEED_USERS,AI_PARTNER].find(u=>u.id===pid);setPartner(p);setShowInvite(false);}}
            onClose={()=>setShowInvite(false)}/>
        )}
        <PreSessionReflection exercise={sel} lang={lang}
          onStart={async (pd)=>{
            // Credit check: 1 credit per session for paid users (freemium is free)
            if (onSpendCredit && effectiveLicense !== 'freemium') {
              const ok = await onSpendCredit(1, `exercise_${sel.id}`);
              if (!ok) { setCreditError(true); return; }
            }
            playSound('exercise_start');
            setPreData(pd);
            // Custom sessions have their own built-in flow — skip prep/trackpick
            if (['snelle_reset','terug_naar_kracht','manipulation_protection'].includes(sel.id)) {
              setView('session');
            } else if (effectiveLicense !== 'freemium' && labTracks.length > 0) {
              setPreData(pd);
              setView('trackpick');
            } else {
              setView("prep");
            }
          }}
          onBack={()=>setView("detail")}/>
        <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${_T.cardBorderBot}`}}>
          <button style={s_GB({width:"100%",fontSize:13})} onClick={()=>setShowInvite(true)}>
            👥 {nl?"Samen oefenen":"Exercise together"}
          </button>
          {partner && (
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8,padding:"8px 12px",background:`${partner.color||_T.accent}12`,borderRadius:12,border:`1px solid ${partner.color||_T.accent}33`}}>
              <Avatar user={partner} size={28}/>
              <span style={{fontSize:12,color:C.white}}>{partner.name} {nl?"doet mee":"joined"}</span>
              <button onClick={()=>setPartner(null)} style={{background:"none",border:"none",color:`${C.red}88`,cursor:"pointer",marginLeft:"auto",fontSize:14}}>✕</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view==="detail" && sel) {
    const wc = exHistory.filter(h=>h.exId===sel.id&&h.date>Date.now()-7*24*3600*1000).length;
    return (
      <div style={{minHeight:"100vh",background:_T.bg}}>
        <style>{CSS}</style>
        <div style={{position:"relative",height:180,overflow:"hidden"}}>
          <div style={{position:"absolute",inset:0,background:`linear-gradient(160deg,${sel.bgColor},#020d0b)`}}/>
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:130,opacity:0.08}}>{sel.bgEmoji}</div>
          <div style={{position:"absolute",bottom:0,left:0,right:0,height:60,background:`linear-gradient(transparent,${_T.bg})`}}/>
          <button onClick={()=>setView("list")} style={{position:"absolute",top:16,left:16,background:"rgba(13,22,37,0.85)",border:"1px solid rgba(255,255,255,0.12)",color:C.silver2,borderRadius:10,padding:"7px 14px",cursor:"pointer",fontSize:13}}>←</button>
          <div style={{position:"absolute",bottom:14,left:20}}>
            <div style={{color:C.white,fontFamily:"Georgia,serif",fontSize:20}}>{nl?sel.label:sel.labelEN}</div>
          </div>
        </div>
        <div style={{padding:18}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            {[{icon:"🧠",label:nl?"Mentaal":"Mental",val:`${sel.mental<0?"-":"+"}${Math.abs(sel.mental)}%`,col:sel.mental<0?C.teal:C.orange},{icon:"💪",label:nl?"Fysiek":"Physical",val:`${sel.physical<0?"-":"+"}${Math.abs(sel.physical)}%`,col:sel.physical<0?C.teal:C.orange},{icon:"🌿",label:nl?"Herstel":"Recovery",val:`+${sel.rest}%`,col:C.teal}].map(s=>(
              <div key={s.label} style={s_GC({padding:12,textAlign:"center",marginBottom:0})}>
                <div style={{fontSize:18,marginBottom:4}}>{s.icon}</div>
                <div style={{fontSize:15,color:s.col,fontWeight:700}}>{s.val}</div>
                <div style={{fontSize:9,color:C.silver,marginTop:2}}>{s.label.toUpperCase()}</div>
              </div>
            ))}
          </div>
          <div style={s_GC({marginBottom:14})}>
            <div style={{color:C.silver2,fontSize:14,lineHeight:1.8}}>{nl?sel.desc:sel.descEN}</div>
          </div>
          <GrowthEffects exerciseId={sel.id} lang={lang}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            <div style={s_GC({padding:14,marginBottom:0,textAlign:"center"})}><div style={{fontSize:22,color:_T.accent,fontWeight:700}}>{sel.duration}m</div><div style={{fontSize:13,color:C.silver}}>{nl?"Duur":"Duration"}</div></div>
            <div style={s_GC({padding:14,marginBottom:0,textAlign:"center"})}><div style={{fontSize:22,color:C.teal,fontWeight:700}}>{wc}x</div><div style={{fontSize:13,color:C.silver}}>{nl?"Deze week":"This week"}</div></div>
          </div>
          <button style={s_PB({width:"100%",fontSize:16,padding:"16px",borderRadius:16})} onClick={()=>setView("prereflect")}>
            ▶ {nl?"Starten":"Start"} →
          </button>
        </div>
      </div>
    );
  }

  const isFreemium = effectiveLicense === 'freemium';

  // Read growth levels for exercise gating
  let catLevels = {};
  try { catLevels = JSON.parse(localStorage.getItem('sl_cat_levels') || '{}'); } catch {}

  const filtered = EXERCISES.filter(ex => {
    if (catFilt !== "all" && ex.categoryGroup !== catFilt) return false;
    return true;
  });
  const byCategory = {};
  filtered.forEach(ex => { if (!byCategory[ex.categoryGroup]) byCategory[ex.categoryGroup]=[]; byCategory[ex.categoryGroup].push(ex); });

  /* ── Tab bar (only shown in list/main view) ── */
  const SCREEN_TABS = [
    { id: 'exercise', icon: '🧘', label: nl ? 'Oefeningen' : 'Exercises' },
    { id: 'read',     icon: '📚', label: nl ? 'Lezen'      : 'Read'      },
    { id: 'programs', icon: '🏆', label: nl ? "Programma's" : 'Programs'  },
  ];

  const tabBar = (
    <div style={{
      display: 'flex',
      background: _T.bg4,
      borderRadius: 16,
      padding: 4,
      marginBottom: 18,
      border: `1px solid ${_T.cardBorderBot}`,
      gap: 2,
    }}>
      {SCREEN_TABS.map(tab => {
        const active = screenTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => { playSound('tab'); setScreenTab(tab.id); }}
            style={{
              flex: 1,
              background: active
                ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})`
                : 'transparent',
              color: active ? '#0a1220' : C.silver,
              border: 'none',
              borderRadius: 12,
              padding: '9px 4px',
              cursor: 'pointer',
              fontWeight: active ? 700 : 400,
              fontSize: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              transition: 'background 0.2s, color 0.2s',
            }}
          >
            <span style={{ fontSize: 16 }}>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );

  /* ── Read tab ── */
  if (screenTab === 'read') {
    return (
      <div>
        {tabBar}
        <ReadTab lang={lang} />
      </div>
    );
  }

  /* ── Programs tab ── */
  if (screenTab === 'programs') {
    const goToExercise = (exId) => {
      const ex = EXERCISES.find(e => e.id === exId);
      if (!ex) return;
      setScreenTab('exercise');
      setSel(ex);
      setView('detail');
    };
    return (
      <div>
        {tabBar}
        <ProgramsTab lang={lang} license={effectiveLicense} exHistory={exHistory} onStartExercise={goToExercise} />
      </div>
    );
  }

  /* ── Exercise tab (default) ── */
  return (
    <>
      {/* Growth level-up queue */}
      {levelUpQueue.length > 0 && (
        <LevelUpModal
          lang={lang}
          category={levelUpQueue[0].category}
          newLevel={levelUpQueue[0].newLevel}
          onClose={() => setLevelUpQueue(q => q.slice(1))}
        />
      )}

      {/* Credit error modal — vertically centered overlay, outside fadeUp to avoid transform clip */}
      {creditError && (
        <div onClick={() => setCreditError(false)} style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: _T.bg3, border: `1px solid ${C.red}55`, borderRadius: 20, padding: '24px 22px', maxWidth: 340, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, boxShadow: '0 16px 48px rgba(0,0,0,0.7)', textAlign: 'center' }}>
            <span style={{ fontSize: 36 }}>💎</span>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 16 }}>
              {nl ? 'Alleen voor betaalde gebruikers' : 'Premium only'}
            </div>
            <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.5 }}>
              {nl ? 'Upgrade je account om deze oefening te ontgrendelen.' : 'Upgrade your account to unlock this exercise.'}
            </div>
            <button style={{ marginTop: 6, background: 'none', border: `1px solid ${C.silver}44`, color: C.silver, cursor: 'pointer', fontSize: 13, padding: '8px 24px', borderRadius: 10 }} onClick={() => setCreditError(false)}>{nl ? 'Sluiten' : 'Close'}</button>
          </div>
        </div>
      )}
    <div className="fadeUp">
      {tabBar}
      {/* ── Resume banner ── */}
      {savedSession && view === 'list' && (
        <div style={{
          background: `linear-gradient(135deg,${savedSession.sel.bgColor}22,${_T.bg3})`,
          border: `1px solid ${savedSession.sel.color}55`,
          borderRadius: 16, padding: '14px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 46, height: 46, borderRadius: 13, flexShrink: 0,
            background: `linear-gradient(160deg,${savedSession.sel.bgColor},#020d0b)`,
            border: `1px solid ${savedSession.sel.color}33`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
          }}>{savedSession.sel.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: savedSession.sel.color, fontWeight: 700, letterSpacing: 1, marginBottom: 2 }}>
              {nl ? '⏸ SESSIE GEPAUZEERD' : '⏸ SESSION PAUSED'}
            </div>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>
              {nl ? savedSession.sel.label : savedSession.sel.labelEN}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              style={s_PB({ padding: '8px 16px', fontSize: 13, borderRadius: 10 })}
              onClick={() => { setSel(savedSession.sel); setPreData(savedSession.preData); setView('session'); }}
            >
              ▶ {nl ? 'Doorgaan' : 'Continue'}
            </button>
            <button
              style={{ background: 'transparent', border: `1px solid ${C.red}44`, color: `${C.red}99`, borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontSize: 11 }}
              onClick={() => { clearSession(); setSavedSession(null); }}
            >
              {nl ? 'Verwijderen' : 'Discard'}
            </button>
          </div>
        </div>
      )}
      <style>{`.ex-car::-webkit-scrollbar{display:none}`}</style>
      <div style={{display:"flex",gap:4,flexWrap:"nowrap",overflowX:"auto",marginBottom:16,paddingBottom:4}}>
        <TapButton style={s_PL(catFilt==="all")} onClick={()=>{playSound('filter');setCatFilt("all");}}>{nl?"Alles":"All"}</TapButton>
        {EXERCISE_CATS.map(cg=><TapButton key={cg.id} style={{...s_PL(catFilt===cg.id,cg.color),flexShrink:0}} onClick={()=>{playSound('filter');setCatFilt(cg.id);}}>{cg.icon} {nl?cg.label:cg.labelEN}</TapButton>)}
      </div>
      {(()=>{
        const EX_THEME={
          focus:              {bg1:'#3d1f00',bg2:'#120800'},
          mindfulness:        {bg1:'#1e1555',bg2:'#0d091e'},
          recovery:           {bg1:'#003322',bg2:'#000e09'},
          movement:           {bg1:'#3d1a00',bg2:'#130700'},
          body:               {bg1:'#3d0028',bg2:'#140010'},
          activation:         {bg1:'#3a2d00',bg2:'#141000'},
          social:             {bg1:'#001840',bg2:'#00091a'},
          perception:         {bg1:'#00183a',bg2:'#000818'},
          emotional_stability:{bg1:'#3d0020',bg2:'#14000c'},
        };
        return Object.entries(byCategory).map(([cgId,exs]) => {
          const cg = EXERCISE_CATS.find(x=>x.id===cgId)||{label:cgId,labelEN:cgId,icon:"🔹",color:_T.accent,id:cgId};
          const th = EX_THEME[cgId]||{bg1:'#1a1a2e',bg2:'#0e0e1a'};
          const c = cg.color;
          return (
            <div key={cgId} style={{marginBottom:28}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,paddingLeft:2}}>
                <div style={{width:30,height:30,borderRadius:9,background:`${c}18`,border:`1.5px solid ${c}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,boxShadow:`0 0 12px ${c}22`}}>{cg.icon}</div>
                <div style={{color:c,fontSize:11,fontWeight:700,letterSpacing:2}}>{(nl?cg.label:cg.labelEN).toUpperCase()}</div>
                <div style={{flex:1,height:1,background:`linear-gradient(90deg,${c}30,transparent)`}}/>
                <span style={{fontSize:11,color:`${c}55`,fontWeight:600}}>{exs.length}</span>
              </div>
              <div
                className="ex-car"
                onScroll={e=>{const n=Date.now();if(!e.currentTarget._t||n-e.currentTarget._t>320){e.currentTarget._t=n;playSound('filter');}}}
                style={{display:"flex",gap:10,overflowX:"auto",scrollSnapType:"x mandatory",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",paddingBottom:6,paddingRight:20}}
              >
                {exs.map(ex=>{
                  const wc=exHistory.filter(h=>h.exId===ex.id&&h.date>Date.now()-7*24*3600*1000).length;
                  const premiumLocked=ex.premium&&isFreemium;
                  const rl=ex.requiredLevel;
                  const curCatLevel=rl?(catLevels[rl.cat]||1):null;
                  const levelLocked=rl&&curCatLevel<rl.level;
                  const locked=premiumLocked||levelLocked;
                  const levelPct=rl?Math.min(100,(curCatLevel/rl.level)*100):0;
                  return (
                    <GlowCard key={ex.id}
                      onClick={()=>{if(premiumLocked){setCreditError(true);return;}if(levelLocked)return;setSel(ex);setView("detail");}}
                      color={`${c}30`}
                      style={{
                        flexShrink:0,scrollSnapAlign:"start",
                        width:168,minHeight:208,borderRadius:22,
                        background:`linear-gradient(155deg,${th.bg1},${th.bg2})`,
                        border:`1.5px solid ${c}${locked?"1a":"40"}`,
                        boxShadow:locked?"none":`0 4px 32px ${c}1a,inset 0 0 50px ${c}08`,
                        cursor:locked&&!premiumLocked?"default":"pointer",
                        opacity:locked?0.72:1,
                        overflow:"hidden",
                        padding:"16px 14px 14px",
                        display:"flex",flexDirection:"column",
                      }}
                    >
                      {/* Watermark */}
                      <div style={{position:"absolute",bottom:-22,right:-10,fontSize:92,opacity:0.055,userSelect:"none",lineHeight:1,pointerEvents:"none",filter:`drop-shadow(0 0 8px ${c})`}}>{cg.icon}</div>
                      {/* Top row */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                        <div style={{
                          width:54,height:54,borderRadius:17,
                          background:`radial-gradient(circle at 38% 38%,${c}2e,${c}0f)`,
                          border:`1.5px solid ${c}${locked?"1a":"50"}`,
                          display:"flex",alignItems:"center",justifyContent:"center",fontSize:25,
                          boxShadow:locked?"none":`0 0 20px ${c}44`,
                          filter:locked?"grayscale(0.65)":"none",
                        }}>{locked?"🔒":ex.icon}</div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                          {ex.duration&&<div style={{fontSize:9,color:c,background:`${c}15`,border:`1px solid ${c}28`,borderRadius:20,padding:"2px 7px",fontWeight:700}}>{ex.duration}m</div>}
                          {premiumLocked&&<div style={{fontSize:9,color:"#C8A84B",background:"rgba(200,168,75,0.12)",border:"1px solid rgba(200,168,75,0.28)",borderRadius:20,padding:"2px 7px",fontWeight:700}}>⭐</div>}
                          {wc>0&&!locked&&<div style={{fontSize:9,color:c,background:`${c}18`,borderRadius:20,padding:"2px 7px",fontWeight:700}}>×{wc}</div>}
                        </div>
                      </div>
                      {/* Title */}
                      <div style={{color:locked?"#5566aa":"#EEF3FF",fontSize:13,fontWeight:700,lineHeight:1.3,marginBottom:8,flex:1}}>{nl?ex.label:ex.labelEN}</div>
                      {/* Bottom */}
                      {levelLocked?(
                        <div>
                          <div style={{fontSize:10,color:"#8899BB",marginBottom:5}}>🔓 {nl?`Niveau ${rl.level} vereist`:`Lvl ${rl.level} req.`}</div>
                          <div style={{height:3,borderRadius:3,background:"rgba(255,255,255,0.07)",overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${levelPct}%`,background:`linear-gradient(90deg,${c}66,${c})`,borderRadius:3}}/>
                          </div>
                          <div style={{fontSize:9,color:"#667799",marginTop:3}}>{curCatLevel}/{rl.level}</div>
                        </div>
                      ):(
                        <div style={{display:"flex",gap:8,paddingTop:6,borderTop:`1px solid ${c}18`}}>
                          <span style={{fontSize:11,color:ex.mental<0?"#2A9E82":"#D4873A"}}>🧠 {ex.mental<0?"-":"+"}{Math.abs(ex.mental)}%</span>
                          <span style={{fontSize:11,color:"#2A9E82"}}>🌿 +{ex.rest}%</span>
                        </div>
                      )}
                    </GlowCard>
                  );
                })}
              </div>
            </div>
          );
        });
      })()}
    </div>
    </>
  );
}

export default ExerciseScreen;
